import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Target,
  GitCompare,
  Layers,
  FileText,
  TrendingUp,
  CheckCircle,
  CircleDot,
  Building2,
  Loader2,
  Search,
  Info,
  Sparkles
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';

interface QuestionWheel {
  id: string;
  name: string;
  category: string;
  description: string;
  totalQuestions: number;
  expertSuitableQuestions: number;
  avgOpportunityScore: number | null;
  maxOpportunityScore: number | null;
}

interface WheelQuestion {
  id: string;
  text: string;
  type: string;
  answerType: string;
  metrics: {
    searchVolume: number;
    cpc: number | null;
    competitionLevel: string;
    opportunityScore: number;
    difficultyScore: number;
    hasFeaturedSnippet: boolean;
    hasPaa: boolean;
    contentGap: boolean;
  };
  source: {
    type: string;
    wheelId: string;
    wheelName: string;
    wheelCategory: string;
  };
}

interface BusinessTemplate {
  id: string;
  key: string;
  name: string;
  description: string;
  questions: { id: string; text: string; category: string; required: boolean }[];
  targetExpertTypes: string[];
  articleStyle: string;
  schemaTypes: string[];
}

interface MatchedQuestion {
  id: string;
  text: string;
  source: { type: string; sourceId: string; wheelName?: string };
  matchScore: number;
  metrics: { searchVolume: number; opportunityScore: number; contentGap: string | null };
  answerType: string | null;
  isUnanswered: boolean;
}

interface ApproachSelectorProps {
  approach: string;
  approachData: any;
  onChange: (approach: string, data: any) => void;
  expertId?: string;
}

const approaches = [
  {
    id: 'product_deep_dive',
    title: 'Product Deep Dive',
    description: 'Comprehensive exploration of a single product',
    icon: Target,
    color: 'bg-blue-500',
    fields: ['product_name', 'product_category', 'key_features'],
    isNew: false
  },
  {
    id: 'expert_matched',
    title: 'Expert Knowledge Match',
    description: 'AI finds questions that match this expert\'s knowledge areas',
    icon: Sparkles,
    color: 'bg-violet-500',
    fields: ['expert_id', 'matched_questions'],
    isNew: true
  },
  {
    id: 'question_wheel',
    title: 'From Question Wheel',
    description: 'Use high-opportunity questions from your research',
    icon: CircleDot,
    color: 'bg-indigo-500',
    fields: ['wheel_id', 'selected_questions'],
    isNew: true
  },
  {
    id: 'business_story',
    title: 'Business Story',
    description: 'Interview founders/executives about the company',
    icon: Building2,
    color: 'bg-amber-500',
    fields: ['template_key', 'selected_questions'],
    isNew: true
  },
  {
    id: 'product_comparison',
    title: 'Product Comparison',
    description: 'Compare multiple products or solutions',
    icon: GitCompare,
    color: 'bg-purple-500',
    fields: ['products', 'comparison_criteria'],
    isNew: false
  },
  {
    id: 'category',
    title: 'Category Overview',
    description: 'Explore an entire product category or market',
    icon: Layers,
    color: 'bg-green-500',
    fields: ['category_name', 'focus_areas'],
    isNew: false
  },
  {
    id: 'selected_questions',
    title: 'Custom Questions',
    description: 'Provide your own list of questions',
    icon: FileText,
    color: 'bg-orange-500',
    fields: ['custom_questions'],
    isNew: false
  },
  {
    id: 'trending_topic',
    title: 'Trending Topic',
    description: 'Discuss a current trend or emerging topic',
    icon: TrendingUp,
    color: 'bg-pink-500',
    fields: ['topic_name', 'trend_context'],
    isNew: false
  }
];

export function ApproachSelector({ approach, approachData, onChange, expertId }: ApproachSelectorProps) {
  const { selectedSite } = useSite();
  const [selectedApproach, setSelectedApproach] = useState(approach);
  const [data, setData] = useState(approachData || {});

  // Question wheel state
  const [wheels, setWheels] = useState<QuestionWheel[]>([]);
  const [selectedWheelId, setSelectedWheelId] = useState<string | null>(data.wheel_id || null);
  const [wheelQuestions, setWheelQuestions] = useState<WheelQuestion[]>([]);
  const [selectedWheelQuestions, setSelectedWheelQuestions] = useState<string[]>(data.selected_wheel_questions || []);
  const [loadingWheels, setLoadingWheels] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Business template state
  const [templates, setTemplates] = useState<BusinessTemplate[]>([]);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(data.template_key || null);
  const [selectedTemplateQuestions, setSelectedTemplateQuestions] = useState<string[]>(data.selected_template_questions || []);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Expert-matched questions state
  const [matchedQuestions, setMatchedQuestions] = useState<MatchedQuestion[]>([]);
  const [selectedMatchedQuestions, setSelectedMatchedQuestions] = useState<string[]>(data.selected_matched_questions || []);
  const [loadingMatched, setLoadingMatched] = useState(false);
  const [matchedTotal, setMatchedTotal] = useState(0);

  // Load wheels and templates when component mounts or approach changes
  useEffect(() => {
    if (selectedApproach === 'question_wheel' && wheels.length === 0) {
      loadWheels();
    }
    if (selectedApproach === 'business_story' && templates.length === 0) {
      loadTemplates();
    }
    if (selectedApproach === 'expert_matched' && expertId) {
      loadMatchedQuestions();
    }
  }, [selectedApproach, expertId]);

  // Load wheel questions when a wheel is selected
  useEffect(() => {
    if (selectedWheelId) {
      loadWheelQuestions(selectedWheelId);
    }
  }, [selectedWheelId]);

  const loadWheels = async () => {
    if (!selectedSite?.id) return;
    try {
      setLoadingWheels(true);
      const response = await authFetch(`/api/interviews/question-sources?siteId=${selectedSite.id}`);
      if (response.ok) {
        const data = await response.json();
        setWheels(data.questionWheels?.wheels || []);
      }
    } catch (err) {
      console.error('[ApproachSelector] Error loading wheels:', err);
    } finally {
      setLoadingWheels(false);
    }
  };

  const loadWheelQuestions = async (wheelId: string) => {
    if (!selectedSite?.id) return;
    try {
      setLoadingQuestions(true);
      const response = await authFetch(
        `/api/interviews/wheel-questions/${wheelId}?siteId=${selectedSite.id}&limit=30`
      );
      if (response.ok) {
        const responseData = await response.json();
        const questions = responseData.questions || [];
        setWheelQuestions(questions);
        // Auto-select top 10 questions by default
        const topQuestions = questions.slice(0, 10).map((q: WheelQuestion) => q.id);
        setSelectedWheelQuestions(topQuestions);
        // Pass questions directly since state won't be updated yet
        handleWheelDataChange(wheelId, topQuestions, questions);
      }
    } catch (err) {
      console.error('[ApproachSelector] Error loading wheel questions:', err);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const loadTemplates = async () => {
    if (!selectedSite?.id) return;
    try {
      setLoadingTemplates(true);
      const response = await authFetch(`/api/interviews/business-templates?siteId=${selectedSite.id}`);
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('[ApproachSelector] Error loading templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadMatchedQuestions = async () => {
    if (!selectedSite?.id || !expertId) return;
    try {
      setLoadingMatched(true);
      const response = await authFetch(
        `/api/interviews/expert-matched-questions?siteId=${selectedSite.id}&expertId=${expertId}&limit=30`
      );
      if (response.ok) {
        const responseData = await response.json();
        const questions: MatchedQuestion[] = responseData.questions || [];
        setMatchedQuestions(questions);
        setMatchedTotal(responseData.totalMatched || questions.length);
        // Auto-select top 10
        const topIds = questions.slice(0, 10).map(q => q.id);
        setSelectedMatchedQuestions(topIds);
        handleMatchedDataChange(topIds, questions);
      }
    } catch (err) {
      console.error('[ApproachSelector] Error loading matched questions:', err);
    } finally {
      setLoadingMatched(false);
    }
  };

  const handleMatchedDataChange = (questionIds: string[], questionsData?: MatchedQuestion[]) => {
    const questionsToUse = questionsData || matchedQuestions;
    const selectedQuestionObjects = questionsToUse
      .filter(q => questionIds.includes(q.id))
      .map((q, idx) => ({
        id: q.id,
        text: q.text,
        category: 'core',
        priority: idx < 5 ? 'high' : 'medium',
        metrics: q.metrics,
        source: 'expert_matched',
        matchScore: q.matchScore,
      }));

    const newData = {
      ...data,
      expert_id: expertId,
      selected_matched_questions: questionIds,
      selected_matched_question_objects: selectedQuestionObjects,
      approachSource: {
        type: 'expert_matched',
        expertId: expertId,
      }
    };
    setData(newData);
    onChange(selectedApproach, newData);
  };

  const toggleMatchedQuestion = (questionId: string) => {
    const newSelection = selectedMatchedQuestions.includes(questionId)
      ? selectedMatchedQuestions.filter(id => id !== questionId)
      : [...selectedMatchedQuestions, questionId];
    setSelectedMatchedQuestions(newSelection);
    handleMatchedDataChange(newSelection);
  };

  const selectTopMatchedQuestions = (count: number) => {
    const topQuestions = matchedQuestions.slice(0, count).map(q => q.id);
    setSelectedMatchedQuestions(topQuestions);
    handleMatchedDataChange(topQuestions);
  };

  const handleApproachSelect = (id: string) => {
    setSelectedApproach(id);
    onChange(id, data);
  };

  const handleDataChange = (field: string, value: string) => {
    const newData = { ...data, [field]: value };
    setData(newData);
    onChange(selectedApproach, newData);
  };

  const handleWheelDataChange = (wheelId: string, questionIds: string[], questionsData?: WheelQuestion[]) => {
    // Use provided questions data or fall back to state (for toggle operations)
    const questionsToUse = questionsData || wheelQuestions;
    // Get full question objects for selected IDs
    const selectedQuestionObjects = questionsToUse
      .filter(q => questionIds.includes(q.id))
      .map((q, idx) => ({
        id: q.id,
        text: q.text,
        category: 'core',
        priority: idx < 5 ? 'high' : 'medium',
        metrics: q.metrics,
        source: 'question_wheel'
      }));

    const newData = {
      ...data,
      wheel_id: wheelId,
      selected_wheel_questions: questionIds,
      selected_wheel_question_objects: selectedQuestionObjects,
      approachSource: {
        type: 'question_wheel',
        wheelId: wheelId,
        wheelName: wheels.find(w => w.id === wheelId)?.name || ''
      }
    };
    setData(newData);
    onChange(selectedApproach, newData);
  };

  const handleTemplateDataChange = (templateKey: string, questionIds: string[]) => {
    const template = templates.find(t => t.key === templateKey);
    // Get full question objects for selected IDs
    const selectedQuestionObjects = template?.questions
      .filter(q => questionIds.includes(q.id))
      .map((q, idx) => ({
        id: q.id,
        text: q.text,
        category: q.category || 'core',
        priority: q.required ? 'high' : 'medium',
        source: 'business_template'
      })) || [];

    const newData = {
      ...data,
      template_key: templateKey,
      selected_template_questions: questionIds,
      selected_template_question_objects: selectedQuestionObjects,
      approachSource: {
        type: 'business_template',
        templateKey: templateKey,
        templateName: template?.name || ''
      }
    };
    setData(newData);
    onChange(selectedApproach, newData);
  };

  const toggleWheelQuestion = (questionId: string) => {
    const newSelection = selectedWheelQuestions.includes(questionId)
      ? selectedWheelQuestions.filter(id => id !== questionId)
      : [...selectedWheelQuestions, questionId];
    setSelectedWheelQuestions(newSelection);
    if (selectedWheelId) {
      handleWheelDataChange(selectedWheelId, newSelection);
    }
  };

  const toggleTemplateQuestion = (questionId: string) => {
    const newSelection = selectedTemplateQuestions.includes(questionId)
      ? selectedTemplateQuestions.filter(id => id !== questionId)
      : [...selectedTemplateQuestions, questionId];
    setSelectedTemplateQuestions(newSelection);
    if (selectedTemplateKey) {
      handleTemplateDataChange(selectedTemplateKey, newSelection);
    }
  };

  const selectTopWheelQuestions = (count: number) => {
    const topQuestions = wheelQuestions.slice(0, count).map(q => q.id);
    setSelectedWheelQuestions(topQuestions);
    if (selectedWheelId) {
      handleWheelDataChange(selectedWheelId, topQuestions);
    }
  };

  const handleTemplateSelect = (templateKey: string) => {
    setSelectedTemplateKey(templateKey);
    const template = templates.find(t => t.key === templateKey);
    if (template) {
      // Auto-select required questions
      const requiredQuestions = template.questions
        .filter(q => q.required)
        .map(q => q.id);
      setSelectedTemplateQuestions(requiredQuestions);
      handleTemplateDataChange(templateKey, requiredQuestions);
    }
  };

  const selectedApproachConfig = approaches.find((a) => a.id === selectedApproach);

  return (
    <div className="space-y-6">
      {/* Approach Selection */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Choose Interview Approach</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {approaches.map((item) => {
            const Icon = item.icon;
            const isSelected = selectedApproach === item.id;

            return (
              <Card
                key={item.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  isSelected ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => handleApproachSelect(item.id)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className={`${item.color} p-2 rounded-lg text-white`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-sm">{item.title}</h4>
                          {item.isNew && (
                            <Badge variant="secondary" className="bg-green-100 text-green-800 text-[10px] px-1.5 py-0">
                              NEW
                            </Badge>
                          )}
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Approach-Specific Fields */}
      {selectedApproachConfig && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {React.createElement(selectedApproachConfig.icon, { className: 'w-5 h-5' })}
              {selectedApproachConfig.title} Details
            </CardTitle>
            <CardDescription>
              Provide additional context for the interview
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Product Deep Dive Fields */}
            {selectedApproach === 'product_deep_dive' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="product_name">Product Name *</Label>
                  <Input
                    id="product_name"
                    placeholder="e.g., Mountain Bike Pro 5000"
                    value={data.product_name || ''}
                    onChange={(e) => handleDataChange('product_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="product_category">Product Category</Label>
                  <Input
                    id="product_category"
                    placeholder="e.g., Mountain Bikes"
                    value={data.product_category || ''}
                    onChange={(e) => handleDataChange('product_category', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="key_features">Key Features</Label>
                  <Textarea
                    id="key_features"
                    placeholder="List the main features to discuss..."
                    value={data.key_features || ''}
                    onChange={(e) => handleDataChange('key_features', e.target.value)}
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Expert Knowledge Match Fields */}
            {selectedApproach === 'expert_matched' && (
              <div className="space-y-4">
                {!expertId ? (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Select an expert first (Step 1) to use this approach. The system will find questions that match their expertise.
                    </AlertDescription>
                  </Alert>
                ) : loadingMatched ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Finding questions that match expert's knowledge...</span>
                  </div>
                ) : matchedQuestions.length === 0 ? (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      No matching questions found. This site may not have question wheels with embeddings yet.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <Alert className="bg-violet-50 border-violet-200">
                      <Sparkles className="h-4 w-4 text-violet-600" />
                      <AlertDescription className="text-violet-800 text-sm">
                        Found <strong>{matchedTotal}</strong> questions matching this expert's knowledge. Questions are ranked by
                        semantic similarity to the expert's profile, combined with search opportunity and content gaps.
                      </AlertDescription>
                    </Alert>

                    <div className="flex items-center justify-between">
                      <Label>Select Questions ({selectedMatchedQuestions.length} selected)</Label>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => selectTopMatchedQuestions(5)}>
                          Top 5
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => selectTopMatchedQuestions(10)}>
                          Top 10
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => selectTopMatchedQuestions(matchedQuestions.length)}>
                          All
                        </Button>
                      </div>
                    </div>

                    <ScrollArea className="h-[300px] rounded-md border p-4">
                      <div className="space-y-3">
                        {matchedQuestions.map((question, idx) => {
                          const isHighMatch = question.matchScore >= 0.5;
                          const isMedMatch = question.matchScore >= 0.35 && question.matchScore < 0.5;

                          return (
                            <div
                              key={question.id}
                              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                selectedMatchedQuestions.includes(question.id)
                                  ? 'bg-primary/5 border-primary'
                                  : 'hover:bg-muted/50'
                              } ${isHighMatch ? 'border-l-4 border-l-violet-500' : isMedMatch ? 'border-l-4 border-l-amber-400' : ''}`}
                              onClick={() => toggleMatchedQuestion(question.id)}
                            >
                              <Checkbox
                                checked={selectedMatchedQuestions.includes(question.id)}
                                onCheckedChange={() => toggleMatchedQuestion(question.id)}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{question.text}</p>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <Badge variant="outline" className="text-xs">
                                    #{idx + 1}
                                  </Badge>
                                  <Badge
                                    variant="secondary"
                                    className={`text-xs ${
                                      isHighMatch
                                        ? 'bg-violet-100 text-violet-800 font-semibold'
                                        : isMedMatch
                                          ? 'bg-amber-100 text-amber-800'
                                          : 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    Match: {(question.matchScore * 100).toFixed(0)}%
                                    {isHighMatch && ' *'}
                                  </Badge>
                                  {question.source.wheelName && (
                                    <Badge variant="outline" className="text-xs text-muted-foreground">
                                      {question.source.wheelName}
                                    </Badge>
                                  )}
                                  {(question.metrics.opportunityScore || 0) > 0 && (
                                    <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                                      Opp: {question.metrics.opportunityScore}
                                    </Badge>
                                  )}
                                  {question.metrics.searchVolume > 0 && (
                                    <Badge variant="outline" className="text-xs text-muted-foreground">
                                      {question.metrics.searchVolume.toLocaleString()} searches/mo
                                    </Badge>
                                  )}
                                  {question.isUnanswered && (
                                    <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                                      Unanswered
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </>
                )}
              </div>
            )}

            {/* Question Wheel Fields */}
            {selectedApproach === 'question_wheel' && (
              <div className="space-y-4">
                {loadingWheels ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading question wheels...</span>
                  </div>
                ) : wheels.length === 0 ? (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      No question wheels available for this site. Create a question wheel first to use this approach.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    {/* Wheel Selector */}
                    <div className="space-y-2">
                      <Label>Select Question Wheel *</Label>
                      <Select value={selectedWheelId || ''} onValueChange={setSelectedWheelId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a question wheel..." />
                        </SelectTrigger>
                        <SelectContent>
                          {wheels.map((wheel) => (
                            <SelectItem key={wheel.id} value={wheel.id}>
                              <div className="flex items-center gap-2">
                                <span>{wheel.name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {wheel.expertSuitableQuestions} questions
                                </Badge>
                                {wheel.avgOpportunityScore && (
                                  <Badge variant="secondary" className="text-xs">
                                    Avg Opp: {wheel.avgOpportunityScore}
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Questions List */}
                    {selectedWheelId && (
                      <div className="space-y-3">
                        <Alert className="bg-blue-50 border-blue-200">
                          <Info className="h-4 w-4 text-blue-600" />
                          <AlertDescription className="text-blue-800 text-sm">
                            Questions are sorted by <strong>opportunity score</strong> and filtered to show only those suitable for expert interviews (opinions, experiences, recommendations). Factual/spec questions are excluded.
                          </AlertDescription>
                        </Alert>

                        <div className="flex items-center justify-between">
                          <Label>Select Questions ({selectedWheelQuestions.length} selected)</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => selectTopWheelQuestions(5)}
                            >
                              Top 5
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => selectTopWheelQuestions(10)}
                            >
                              Top 10
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => selectTopWheelQuestions(wheelQuestions.length)}
                            >
                              All
                            </Button>
                          </div>
                        </div>

                        {loadingQuestions ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            <span className="ml-2 text-sm text-muted-foreground">Loading questions...</span>
                          </div>
                        ) : (
                          <ScrollArea className="h-[300px] rounded-md border p-4">
                            <div className="space-y-3">
                              {wheelQuestions.map((question, idx) => {
                                const isHighValue = (question.metrics.opportunityScore || 0) >= 70;
                                const isMediumValue = (question.metrics.opportunityScore || 0) >= 40 && (question.metrics.opportunityScore || 0) < 70;
                                const answerTypeLabel = question.answerType ?
                                  question.answerType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) :
                                  'Expert Opinion';

                                return (
                                  <div
                                    key={question.id}
                                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                      selectedWheelQuestions.includes(question.id)
                                        ? 'bg-primary/5 border-primary'
                                        : 'hover:bg-muted/50'
                                    } ${isHighValue ? 'border-l-4 border-l-green-500' : isMediumValue ? 'border-l-4 border-l-amber-400' : ''}`}
                                    onClick={() => toggleWheelQuestion(question.id)}
                                  >
                                    <Checkbox
                                      checked={selectedWheelQuestions.includes(question.id)}
                                      onCheckedChange={() => toggleWheelQuestion(question.id)}
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium">{question.text}</p>
                                      <div className="flex flex-wrap gap-2 mt-2">
                                        {/* Rank badge */}
                                        <Badge variant="outline" className="text-xs">
                                          #{idx + 1}
                                        </Badge>

                                        {/* Answer type - why this needs an expert */}
                                        <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-800">
                                          {answerTypeLabel}
                                        </Badge>

                                        {/* Opportunity score - prominent for high value */}
                                        {(question.metrics.opportunityScore || 0) > 0 && (
                                          <Badge
                                            variant="secondary"
                                            className={`text-xs ${
                                              isHighValue
                                                ? 'bg-green-100 text-green-800 font-semibold'
                                                : isMediumValue
                                                  ? 'bg-amber-100 text-amber-800'
                                                  : 'bg-gray-100 text-gray-700'
                                            }`}
                                          >
                                            Opp: {question.metrics.opportunityScore}
                                            {isHighValue && ' ★'}
                                          </Badge>
                                        )}

                                        {/* Search volume */}
                                        {question.metrics.searchVolume > 0 && (
                                          <Badge variant="outline" className="text-xs text-muted-foreground">
                                            {question.metrics.searchVolume.toLocaleString()} searches/mo
                                          </Badge>
                                        )}

                                        {/* Content gap indicator */}
                                        {question.metrics.contentGap && (
                                          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                                            Content Gap
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </ScrollArea>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Business Story Fields */}
            {selectedApproach === 'business_story' && (
              <div className="space-y-4">
                {loadingTemplates ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading templates...</span>
                  </div>
                ) : templates.length === 0 ? (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      No business interview templates available. Please contact support.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    {/* Template Selector */}
                    <div className="space-y-2">
                      <Label>Interview Template *</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {templates.map((template) => (
                          <Card
                            key={template.key}
                            className={`cursor-pointer transition-all hover:shadow-sm ${
                              selectedTemplateKey === template.key
                                ? 'ring-2 ring-primary border-primary'
                                : ''
                            }`}
                            onClick={() => handleTemplateSelect(template.key)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="font-medium text-sm">{template.name}</h4>
                                {selectedTemplateKey === template.key && (
                                  <CheckCircle className="w-4 h-4 text-primary" />
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">{template.description}</p>
                              <div className="flex gap-1 mt-2">
                                <Badge variant="outline" className="text-xs">
                                  {template.questions.length} questions
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>

                    {/* Template Questions */}
                    {selectedTemplateKey && (
                      <div className="space-y-3">
                        <Label>Questions to Include ({selectedTemplateQuestions.length} selected)</Label>
                        <div className="space-y-2">
                          {templates
                            .find(t => t.key === selectedTemplateKey)
                            ?.questions.map((question) => (
                              <div
                                key={question.id}
                                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                  selectedTemplateQuestions.includes(question.id)
                                    ? 'bg-primary/5 border-primary'
                                    : 'hover:bg-muted/50'
                                }`}
                                onClick={() => toggleTemplateQuestion(question.id)}
                              >
                                <Checkbox
                                  checked={selectedTemplateQuestions.includes(question.id)}
                                  onCheckedChange={() => toggleTemplateQuestion(question.id)}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm">{question.text}</p>
                                  <div className="flex gap-2 mt-1">
                                    <Badge variant="outline" className="text-xs capitalize">
                                      {question.category}
                                    </Badge>
                                    {question.required && (
                                      <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">
                                        Required
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Product Comparison Fields */}
            {selectedApproach === 'product_comparison' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="products">Products to Compare *</Label>
                  <Textarea
                    id="products"
                    placeholder="List products to compare (one per line)..."
                    value={data.products || ''}
                    onChange={(e) => handleDataChange('products', e.target.value)}
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter each product on a new line
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="comparison_criteria">Comparison Criteria</Label>
                  <Textarea
                    id="comparison_criteria"
                    placeholder="What aspects should we compare? (price, features, performance, etc.)"
                    value={data.comparison_criteria || ''}
                    onChange={(e) => handleDataChange('comparison_criteria', e.target.value)}
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Category Overview Fields */}
            {selectedApproach === 'category' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="category_name">Category Name *</Label>
                  <Input
                    id="category_name"
                    placeholder="e.g., Outdoor Camping Gear"
                    value={data.category_name || ''}
                    onChange={(e) => handleDataChange('category_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="focus_areas">Focus Areas</Label>
                  <Textarea
                    id="focus_areas"
                    placeholder="What aspects of the category should we explore?"
                    value={data.focus_areas || ''}
                    onChange={(e) => handleDataChange('focus_areas', e.target.value)}
                    rows={4}
                  />
                </div>
              </>
            )}

            {/* Custom Questions Fields */}
            {selectedApproach === 'selected_questions' && (
              <div className="space-y-2">
                <Label htmlFor="custom_questions">Your Questions *</Label>
                <Textarea
                  id="custom_questions"
                  placeholder="Enter your questions (one per line)..."
                  value={data.custom_questions || ''}
                  onChange={(e) => handleDataChange('custom_questions', e.target.value)}
                  rows={8}
                />
                <p className="text-xs text-muted-foreground">
                  AI will refine these questions and add follow-ups
                </p>
              </div>
            )}

            {/* Trending Topic Fields */}
            {selectedApproach === 'trending_topic' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="topic_name">Topic/Trend Name *</Label>
                  <Input
                    id="topic_name"
                    placeholder="e.g., Sustainable Product Design"
                    value={data.topic_name || ''}
                    onChange={(e) => handleDataChange('topic_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trend_context">Trend Context</Label>
                  <Textarea
                    id="trend_context"
                    placeholder="Why is this topic trending? What's the current state?"
                    value={data.trend_context || ''}
                    onChange={(e) => handleDataChange('trend_context', e.target.value)}
                    rows={4}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
